// ─── Admin Authentication Service ────────────────────────────────────────────
// Uses Supabase Auth so the browser client holds a real JWT.
// That JWT is automatically attached to every supabase.from(...) query,
// satisfying RLS policies that check auth.role() = 'authenticated'.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from "../lib/supabaseClient";

/** Write to security_audit_log via SECURITY DEFINER RPC (callable by any role) */
async function writeAuditEvent(eventType, userEmail, metadata = {}) {
  try {
    await supabase.rpc("write_audit_event", {
      p_event_type:    eventType,
      p_user_email:    userEmail || null,
      p_resource_type: "auth",
      p_metadata:      Object.keys(metadata).length ? metadata : null,
    });
  } catch (e) {
    // Audit log failure must never break the login flow
    console.warn("[audit]", eventType, e?.message);
  }
}

/**
 * Sign in with email + password via Supabase Auth.
 * On success the supabase client caches the session in localStorage and
 * attaches the JWT to all subsequent DB requests automatically.
 */
export async function loginAdmin(email, password) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      writeAuditEvent("admin_login_failure", email, { reason: error.message });
      return { data: null, error: error.message };
    }
    writeAuditEvent("admin_login_success", email);
    return { data: data.user, error: null };
  } catch (err) {
    writeAuditEvent("admin_login_failure", email, { reason: err.message });
    return { data: null, error: err.message || "Sign-in failed" };
  }
}

/**
 * Return the current session user, or null if not signed in.
 * Supabase restores the session from localStorage automatically on init.
 */
export async function getCurrentAdmin() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) return { data: session.user, error: null };
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
}

/**
 * Sign out and clear the session.
 */
export async function logoutAdmin() {
  try {
    await supabase.auth.signOut();
    return { data: null, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
}
